// client/src/components/pages/LexiStar/Reports/ReportsCentre.tsx
//
// /lexistar/reports — the Reports centre (ST-00188 M4-R2; FR-RPT-002/004/005/
// 008; m4-contracts §6–§7). Page permission lexistar.reports.view; schedules
// are changed with lexistar.reports.manage.
//
//   Reports    the standard report set (report_definitions): open one, or run it
//              now in a format (parameters from its JSON schema) to download.
//   Runs       run history (report_runs) with state — queued, rendering, done,
//              failed — and a download per artefact; refreshes while any run is
//              still going.
//   Schedules  report_schedules: cadence in words, recipients, channels,
//              formats, last / next run, active; run now; create and edit
//              (ScheduleEditor).

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaCalendarAlt, FaDownload, FaFileAlt, FaHistory, FaPlay, FaPlus, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { PageHeader, SegmentedControl, ThemedSelect, useAuth } from '../../../../types/commonComponents';
import { fmtDateTime, fmtDuration } from '../StatusBoard/format';
import ParameterForm, { inputClass } from './ParameterForm';
import ScheduleEditor from './ScheduleEditor';
import {
  errorText,
  listDefinitions,
  listGroupOptions,
  listRuns,
  listSchedules,
  listUserOptions,
  resolveDownload,
  runReport,
  runScheduleNow,
  startDownload,
  updateSchedule,
  type UserOption,
} from '../../../../services/lexistarReports';
import {
  DEDICATED_PAGES,
  FORMAT_LABELS,
  RECIPIENT_KINDS,
  defaultParams,
  CHANNELS,
  describeCron,
  describeScope,
  missingParams,
  pageFor,
  relativeLabel,
  schemaFields,
} from './reportLogic';
import { yesterdaySast } from './dailyLogic';
import { toolbarButton, toolbarPrimary } from './reportAtoms';
import type { ReportDefinition, ReportFormat, ReportRun, ReportSchedule } from '../../../../types/lexistar-reports';

type Tab = 'reports' | 'runs' | 'schedules';
const TABS: Tab[] = ['reports', 'runs', 'schedules'];
const card = 'bg-surface rounded-xl border border-slate-200 shadow-sm';
const POLL_MS = 5000;

const RUN_STATE: Record<string, { label: string; className: string; glyph: string }> = {
  queued: { label: 'Queued', className: 'border border-dashed border-slate-400 text-slate-600', glyph: '◌' },
  rendering: { label: 'Rendering', className: 'border border-slate-300 bg-slate-100 text-slate-700', glyph: '◐' },
  done: { label: 'Done', className: 'border border-slate-300 bg-surface text-slate-800', glyph: '✓' },
  failed: { label: 'Failed', className: 'border border-red-300 bg-red-50 text-red-700', glyph: '✖' },
};

function RunStateChip({ state }: { state: string }) {
  const meta = RUN_STATE[state] || RUN_STATE.queued!;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${meta.className}`} data-state={state}>
      <span aria-hidden="true">{meta.glyph}</span>
      {meta.label}
    </span>
  );
}

/** "Period: last week · Trip: 7c1f…". */
function paramsText(params: Record<string, unknown> | null | undefined): string {
  if (!params) return '—';
  const parts: string[] = [];
  if (typeof params.period === 'string') parts.push(relativeLabel(params.period));
  if (typeof params.from === 'string' && typeof params.to === 'string') parts.push(params.from === params.to ? params.from : `${params.from} – ${params.to}`);
  if (typeof params.date === 'string') parts.push(params.date);
  Object.entries(params).forEach(([k, v]) => {
    if (['period', 'from', 'to', 'date', 'scope'].includes(k) || v === null || v === undefined || v === '') return;
    const s = Array.isArray(v) ? v.join(', ') : String(v);
    parts.push(`${k.replace(/_/g, ' ')}: ${/^[0-9a-f-]{36}$/i.test(s) ? `${s.slice(0, 8)}…` : s}`);
  });
  const scope = describeScope(params.scope as Record<string, unknown> | undefined);
  if (scope) parts.push(scope);
  return parts.join(' · ') || '—';
}

function Banner({ tone, children, onDismiss }: { tone: 'error' | 'notice'; children: ReactNode; onDismiss: () => void }) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`mb-4 px-4 py-3 rounded-lg flex items-start gap-3 text-sm ${tone === 'error' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-800'}`}
    >
      <p className="flex-1">{children}</p>
      <button type="button" onClick={onDismiss} aria-label="Dismiss" className="opacity-60 hover:opacity-100">
        <FaTimes size={12} />
      </button>
    </div>
  );
}

// ── Run-now dialog ──────────────────────────────────────────────────────────

function RunDialog({
  def,
  onClose,
  onStarted,
}: {
  def: ReportDefinition | null;
  onClose: () => void;
  onStarted: (run: ReportRun) => void;
}) {
  const fields = useMemo(() => schemaFields(def?.parameters), [def]);
  const formats = (def?.formats || []).filter((f): f is ReportFormat => f !== 'html');
  const [params, setParams] = useState<Record<string, unknown>>({});
  const [chosen, setChosen] = useState<ReportFormat[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!def) return;
    const periodic = fields.some((f) => f.kind === 'daterange' || f.name === 'date');
    setParams({ ...(periodic ? { period: 'yesterday' } : {}), ...defaultParams(def.parameters) });
    setChosen(formats.slice(0, 1));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def]);
  if (!def) return null;
  const missing = missingParams(fields, params);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      onStarted(await runReport(def.key, { parameters: params, formats: chosen }));
    } catch (err) {
      setError(errorText(err, 'The report could not be started.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Run ${def.name}`}
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-visible"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Run {def.name}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600">
            <FaTimes size={16} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          <ParameterForm fields={fields} value={params} onChange={setParams} idPrefix={`run-${def.key}`} allowRelative />
          <fieldset>
            <legend className="text-xs font-semibold text-slate-600 mb-1">Formats</legend>
            <div className="flex flex-wrap gap-x-4">
              {formats.map((f) => (
                <label key={f} className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={chosen.includes(f)}
                    onChange={(e) => setChosen((c) => (e.target.checked ? [...c, f] : c.filter((x) => x !== f)))}
                  />
                  {FORMAT_LABELS[f]}
                </label>
              ))}
            </div>
          </fieldset>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-2xl">
          {missing.length > 0 && <span className="text-xs text-slate-500 mr-auto">Needs: {missing.join(', ')}</span>}
          <button type="button" onClick={onClose} className={toolbarButton}>
            Cancel
          </button>
          <button type="button" onClick={go} disabled={busy || missing.length > 0 || !chosen.length} className={toolbarPrimary}>
            <FaPlay size={10} /> {busy ? 'Starting…' : 'Run now'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tabs ────────────────────────────────────────────────────────────────────

function Catalogue({
  definitions,
  canManage,
  onRun,
  onSchedule,
}: {
  definitions: ReportDefinition[];
  canManage: boolean;
  onRun: (d: ReportDefinition) => void;
  onSchedule: (d: ReportDefinition) => void;
}) {
  const documents = definitions.filter((d) => DEDICATED_PAGES[d.key]);
  const standard = definitions.filter((d) => !DEDICATED_PAGES[d.key]);
  const openLink = (d: ReportDefinition) =>
    d.key === 'daily_management_summary'
      ? pageFor(d, { date: yesterdaySast() }) || '/lexistar/reports/daily'
      : `/lexistar/reports/${encodeURIComponent(d.key)}`;
  const item = (d: ReportDefinition) => (
    <li key={d.key} className={`${card} p-4 flex flex-col gap-2`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-900">
          <Link to={openLink(d)} className="hover:underline">
            {d.name}
          </Link>
        </h3>
        <span className="text-[11px] text-slate-500 whitespace-nowrap">
          {d.formats.map((f) => FORMAT_LABELS[f] || f).join(' · ')}
        </span>
      </div>
      {d.description && <p className="text-xs text-slate-600 leading-relaxed">{d.description}</p>}
      <div className="mt-auto pt-1 flex flex-wrap gap-2">
        <Link to={openLink(d)} className={toolbarButton}>
          <FaFileAlt size={11} /> Open
        </Link>
        {d.formats.some((f) => f !== 'html') && (
          <button type="button" className={toolbarButton} onClick={() => onRun(d)} aria-label={`Run ${d.name}`}>
            <FaPlay size={10} /> Run…
          </button>
        )}
        {canManage && d.key !== 'trip_report' && d.key !== 'trip_reconciliation_certificate' && (
          <button type="button" className={toolbarButton} onClick={() => onSchedule(d)} aria-label={`Schedule ${d.name}`}>
            <FaCalendarAlt size={11} /> Schedule…
          </button>
        )}
      </div>
    </li>
  );
  return (
    <div className="space-y-6">
      <section aria-label="Documents">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Documents</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">{documents.map(item)}</ul>
      </section>
      <section aria-label="Standard reports">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Standard reports</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">{standard.map(item)}</ul>
      </section>
    </div>
  );
}

function Runs({
  runs,
  names,
  loading,
  filter,
  setFilter,
  definitions,
  onRefresh,
  highlight,
  onError,
}: {
  runs: ReportRun[];
  names: Map<string, string>;
  loading: boolean;
  filter: { report_key: string; state: string };
  setFilter: (f: { report_key: string; state: string }) => void;
  definitions: ReportDefinition[];
  onRefresh: () => void;
  highlight: string | null;
  onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const download = async (run: ReportRun, format: ReportFormat) => {
    setBusy(`${run.id}:${format}`);
    try {
      const link = await resolveDownload(run.id, format);
      startDownload(link.url, link.filename || undefined);
    } catch (err) {
      onError(errorText(err, 'The download could not be started.'));
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className={card} aria-label="Report runs">
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
        <div className="w-60">
          <ThemedSelect aria-label="Filter by report" value={filter.report_key} onChange={(e) => setFilter({ ...filter, report_key: String(e.target.value) })} className={inputClass}>
            <option value="">All reports</option>
            {definitions.map((d) => (
              <option key={d.key} value={d.key}>
                {d.name}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div className="w-44">
          <ThemedSelect aria-label="Filter by state" value={filter.state} onChange={(e) => setFilter({ ...filter, state: String(e.target.value) })} className={inputClass}>
            <option value="">Any state</option>
            {Object.entries(RUN_STATE).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <button type="button" className={`${toolbarButton} ml-auto`} onClick={onRefresh} aria-label="Refresh runs">
          <FaSyncAlt size={11} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>
      {runs.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-slate-500">
          {loading ? 'Loading runs…' : 'No report runs yet. Run a report or wait for a schedule.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-4 py-2 font-semibold">Report</th>
                <th className="px-4 py-2 font-semibold">Parameters</th>
                <th className="px-4 py-2 font-semibold">By</th>
                <th className="px-4 py-2 font-semibold">State</th>
                <th className="px-4 py-2 font-semibold">Requested</th>
                <th className="px-4 py-2 font-semibold text-right">Took</th>
                <th className="px-4 py-2 font-semibold">Downloads</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const took =
                  r.started_at && r.finished_at ? (Date.parse(r.finished_at) - Date.parse(r.started_at)) / 1000 : null;
                const artefacts = (r.artefacts || []).filter((a) => ['pdf', 'xlsx', 'csv', 'html'].includes(a.format));
                return (
                  <tr key={r.id} className={`border-b border-slate-100 align-top ${highlight === r.id ? 'bg-primary-50' : ''}`} data-run={r.id}>
                    <td className="px-4 py-2.5 font-medium text-slate-900">{names.get(r.report_key) || r.report_key}</td>
                    <td className="px-4 py-2.5 text-slate-600 max-w-xs">{paramsText(r.parameters)}</td>
                    <td className="px-4 py-2.5 text-slate-600">{r.schedule_id ? 'Schedule' : r.requested_by || '—'}</td>
                    <td className="px-4 py-2.5">
                      <RunStateChip state={r.state} />
                      {r.state === 'failed' && r.error && <p className="mt-1 text-xs text-red-700 max-w-xs">{r.error}</p>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 tabular-nums whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                    <td className="px-4 py-2.5 text-slate-600 tabular-nums text-right">{took !== null ? fmtDuration(took) : '—'}</td>
                    <td className="px-4 py-2.5">
                      {r.state === 'done' && artefacts.length ? (
                        <div className="flex flex-wrap gap-1.5">
                          {artefacts.map((a) => (
                            <button
                              key={a.format}
                              type="button"
                              onClick={() => download(r, a.format)}
                              disabled={busy === `${r.id}:${a.format}`}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
                              aria-label={`Download ${FORMAT_LABELS[a.format]} of ${names.get(r.report_key) || r.report_key}`}
                            >
                              <FaDownload size={9} /> {FORMAT_LABELS[a.format]}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">{r.state === 'failed' ? '—' : r.state === 'done' ? 'none' : 'when done'}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function recipientsText(s: ReportSchedule, users: UserOption[], groups: UserOption[]): string {
  if (!s.recipients.length) return 'No recipients';
  return s.recipients
    .map((r) => {
      const ref = String(r.ref);
      const name =
        r.kind === 'user'
          ? users.find((u) => u.id === ref)?.label || `user ${ref}`
          : r.kind === 'group'
            ? groups.find((g) => g.id === ref)?.label || `group ${ref.slice(0, 8)}`
            : ref;
      const scope = describeScope(r.scope);
      return `${name}${scope ? ` (${scope})` : ''}`;
    })
    .join(', ');
}

function Schedules({
  schedules,
  names,
  loading,
  canManage,
  users,
  groups,
  onNew,
  onEdit,
  onRunNow,
  onToggle,
}: {
  schedules: ReportSchedule[];
  names: Map<string, string>;
  loading: boolean;
  canManage: boolean;
  users: UserOption[];
  groups: UserOption[];
  onNew: () => void;
  onEdit: (s: ReportSchedule) => void;
  onRunNow: (s: ReportSchedule) => void;
  onToggle: (s: ReportSchedule) => void;
}) {
  return (
    <section className={card} aria-label="Report schedules">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
        <p className="text-sm text-slate-600">
          Reports sent on a schedule by email, Telegram or in-app — daily, weekly, monthly or at shift end.
        </p>
        {canManage && (
          <button type="button" className={toolbarPrimary} onClick={onNew}>
            <FaPlus size={10} /> New schedule
          </button>
        )}
      </div>
      {schedules.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-slate-500">
          {loading ? 'Loading schedules…' : 'No schedules yet.'}
          {!loading && canManage && ' Create one to send a report on a cadence.'}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {schedules.map((s) => (
            <li key={s.id} className="px-4 py-3 flex flex-col lg:flex-row lg:items-center gap-3" data-schedule={s.id}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-900">{s.name}</span>
                  <span className="text-xs text-slate-500">{names.get(s.report_key) || s.report_key}</span>
                  {!s.active && (
                    <span className="px-1.5 py-px rounded text-[11px] font-medium border border-dashed border-slate-400 text-slate-600">Paused</span>
                  )}
                </div>
                <p className="text-sm text-slate-700 mt-0.5">
                  {describeCron(s.cron)}
                  {s.tz && s.tz !== 'Africa/Johannesburg' ? ` (${s.tz})` : ''} · {paramsText(s.parameters)} ·{' '}
                  {s.formats.map((f) => FORMAT_LABELS[f] || f).join(' + ')} by{' '}
                  {s.channels.map((c) => CHANNELS.find((x) => x.value === c)?.label || c).join(', ')}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 truncate" title={recipientsText(s, users, groups)}>
                  To {recipientsText(s, users, groups)} ·{' '}
                  {s.recipients.length
                    ? [...new Set(s.recipients.map((r) => RECIPIENT_KINDS.find((k) => k.value === r.kind)?.label.replace(/^An? /, '') || r.kind))].join(', ')
                    : ''}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 tabular-nums">
                  Last run {s.last_run_at ? fmtDateTime(s.last_run_at) : 'never'} · next {s.active && s.next_run_at ? fmtDateTime(s.next_run_at) : '—'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" className={toolbarButton} onClick={() => onRunNow(s)} aria-label={`Run ${s.name} now`}>
                  <FaPlay size={10} /> Run now
                </button>
                {canManage && (
                  <>
                    <button type="button" className={toolbarButton} onClick={() => onToggle(s)} aria-label={`${s.active ? 'Pause' : 'Resume'} ${s.name}`}>
                      {s.active ? 'Pause' : 'Resume'}
                    </button>
                    <button type="button" className={toolbarButton} onClick={() => onEdit(s)} aria-label={`Edit ${s.name}`}>
                      Edit
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── The page ────────────────────────────────────────────────────────────────

export default function ReportsCentre() {
  const [search, setSearch] = useSearchParams();
  const tab: Tab = TABS.includes(search.get('tab') as Tab) ? (search.get('tab') as Tab) : 'reports';
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.reports.view');
  const canManage = hasPermission('lexistar.reports.manage');

  const [definitions, setDefinitions] = useState<ReportDefinition[]>([]);
  const [runs, setRuns] = useState<ReportRun[]>([]);
  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [groups, setGroups] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState({ defs: true, runs: false, schedules: false });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState({ report_key: '', state: '' });
  const [runDef, setRunDef] = useState<ReportDefinition | null>(null);
  const [editor, setEditor] = useState<{ open: boolean; schedule: ReportSchedule | null; reportKey: string | null }>({
    open: false,
    schedule: null,
    reportKey: null,
  });
  const [highlight, setHighlight] = useState<string | null>(null);

  const names = useMemo(() => new Map(definitions.map((d) => [d.key, d.name])), [definitions]);
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(search);
    next.set('tab', t);
    setSearch(next, { replace: true });
  };

  useEffect(() => {
    if (!canView) return;
    listDefinitions()
      .then(setDefinitions)
      .catch((err) => setError(errorText(err, 'The report catalogue could not be loaded.')))
      .finally(() => setLoading((l) => ({ ...l, defs: false })));
  }, [canView]);

  const loadRuns = useCallback(
    async (quiet = false) => {
      if (!canView) return;
      if (!quiet) setLoading((l) => ({ ...l, runs: true }));
      try {
        const res = await listRuns({ report_key: filter.report_key || undefined, state: filter.state || undefined, limit: 100 });
        setRuns(res.runs);
      } catch (err) {
        if (!quiet) setError(errorText(err, 'The run history could not be loaded.'));
      } finally {
        if (!quiet) setLoading((l) => ({ ...l, runs: false }));
      }
    },
    [canView, filter.report_key, filter.state]
  );

  const loadSchedules = useCallback(async () => {
    if (!canView) return;
    setLoading((l) => ({ ...l, schedules: true }));
    try {
      setSchedules(await listSchedules());
    } catch (err) {
      setError(errorText(err, 'The schedules could not be loaded.'));
    } finally {
      setLoading((l) => ({ ...l, schedules: false }));
    }
  }, [canView]);

  useEffect(() => {
    if (tab === 'runs') loadRuns();
    if (tab === 'schedules') loadSchedules();
  }, [tab, loadRuns, loadSchedules]);

  // Keep the history moving while a run is queued or rendering.
  const pending = runs.some((r) => r.state === 'queued' || r.state === 'rendering');
  useEffect(() => {
    if (tab !== 'runs' || !pending) return undefined;
    const t = setInterval(() => loadRuns(true), POLL_MS);
    return () => clearInterval(t);
  }, [tab, pending, loadRuns]);

  // Reference lists for the recipients (only when schedules can be managed or shown).
  useEffect(() => {
    if (!canView || tab !== 'schedules') return;
    if (canManage) {
      listUserOptions().then(setUsers).catch(() => setUsers([]));
    }
    listGroupOptions().then(setGroups).catch(() => setGroups([]));
  }, [canView, canManage, tab]);

  if (!canView) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PageHeader icon={FaFileAlt} title="Reports" />
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
          <div role="alert" className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg">
            You do not have permission to view reports.
          </div>
        </div>
      </div>
    );
  }

  const onStarted = (run: ReportRun) => {
    setRunDef(null);
    setHighlight(run.id);
    setNotice(`${names.get(run.report_key) || run.report_key} is ${run.state === 'queued' ? 'queued' : run.state} — it appears under Runs with its downloads when done.`);
    setRuns((rs) => [run, ...rs.filter((r) => r.id !== run.id)]);
    setTab('runs');
  };

  const runNow = async (s: ReportSchedule) => {
    try {
      const run = await runScheduleNow(s.id);
      setNotice(`${s.name} is running now — recipients get it when it is done.`);
      if (run) setHighlight(run.id);
    } catch (err) {
      setError(errorText(err, 'The schedule could not be run.'));
    }
  };

  const toggle = async (s: ReportSchedule) => {
    try {
      const saved = await updateSchedule(s.id, { active: !s.active, ...(s.version ? { version: s.version } : {}) });
      setSchedules((list) => list.map((x) => (x.id === s.id ? { ...s, ...saved, active: saved.id ? saved.active : !s.active } : x)));
    } catch (err) {
      setError(errorText(err, 'The schedule could not be changed.'));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaFileAlt}
        title="Reports"
        subtitle="Documents, standard reports, runs and schedules"
        toolbar={
          <SegmentedControl<Tab>
            ariaLabel="Reports sections"
            value={tab}
            onChange={(v) => setTab(v)}
            options={[
              { value: 'reports', label: 'Reports', icon: FaFileAlt },
              { value: 'runs', label: 'Runs', icon: FaHistory, badge: pending ? '…' : undefined },
              { value: 'schedules', label: 'Schedules', icon: FaCalendarAlt },
            ]}
          />
        }
        actions={
          <Link to={`/lexistar/reports/daily/${yesterdaySast()}`} className={toolbarButton}>
            Yesterday’s summary
          </Link>
        }
      />
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-6">
        {error && (
          <Banner tone="error" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {notice && (
          <Banner tone="notice" onDismiss={() => setNotice(null)}>
            {notice}
          </Banner>
        )}
        {tab === 'reports' &&
          (loading.defs ? (
            <p className="py-10 text-center text-sm text-slate-500">Loading the report catalogue…</p>
          ) : (
            <Catalogue
              definitions={definitions}
              canManage={canManage}
              onRun={setRunDef}
              onSchedule={(d) => {
                setTab('schedules');
                setEditor({ open: true, schedule: null, reportKey: d.key });
              }}
            />
          ))}
        {tab === 'runs' && (
          <Runs
            runs={runs}
            names={names}
            loading={loading.runs}
            filter={filter}
            setFilter={setFilter}
            definitions={definitions}
            onRefresh={() => loadRuns()}
            highlight={highlight}
            onError={setError}
          />
        )}
        {tab === 'schedules' && (
          <Schedules
            schedules={schedules}
            names={names}
            loading={loading.schedules}
            canManage={canManage}
            users={users}
            groups={groups}
            onNew={() => setEditor({ open: true, schedule: null, reportKey: null })}
            onEdit={(s) => setEditor({ open: true, schedule: s, reportKey: s.report_key })}
            onRunNow={runNow}
            onToggle={toggle}
          />
        )}
      </div>
      <RunDialog def={runDef} onClose={() => setRunDef(null)} onStarted={onStarted} />
      <ScheduleEditor
        open={editor.open}
        schedule={editor.schedule}
        definitions={definitions.filter((d) => d.key !== 'trip_report' && d.key !== 'trip_reconciliation_certificate')}
        initialReportKey={editor.reportKey}
        users={users}
        groups={groups}
        onClose={() => setEditor({ open: false, schedule: null, reportKey: null })}
        onSaved={(saved) => {
          setEditor({ open: false, schedule: null, reportKey: null });
          setNotice(`Saved “${saved.name}”.`);
          loadSchedules();
        }}
      />
    </div>
  );
}
